import { DragDropProvider } from "@dnd-kit/react";
import { useDisclosure } from "@mantine/hooks";
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { useParams } from "next/navigation";
import { useCallback, useMemo, useState } from "react";

import {
  formatAlternativeCompetencyColumns,
  formatAssessmentQuestionColumns,
  getColumnVisibilityState,
} from "./columns";
import { TableWrapper, type TableEmptyState } from "./table-wrapper";
import { SortableTableRow } from "@/components/sortable-table-row";
import { useTrackAnalytics } from "@/hooks/analytics.hooks";
import { FEATURE_FLAGS, useFeatureIsOn } from "@/utils/growthbook";
import { useGetDragEndEventHandler } from "@/hooks/dnd-kit.hooks";
import { type ReviewPageCompetency } from "@/types/screener-builder";
import { buildTestId } from "@/utils/helpers";
import {
  screenerHasMaxIncludedCompetencies,
  MAX_COMPETENCIES_TOOLTIP_MESSAGE,
} from "@/utils/screener-builder";

interface AssessmentQuestionTableProps {
  addQuestionTestId?: string;
  /** Override for the max-competencies check. When provided, uses this value
   *  instead of computing from the (potentially filtered) competencies prop.
   *  Pass the check computed from ALL competencies to avoid subsets allowing
   *  users to exceed the global limit. */
  atMaxCompetencies?: boolean;
  /** Override for the minimum-assessment-question check, computed from ALL
   *  competencies. Needed when voice/written render as separate tables so a
   *  subset doesn't wrongly block (or skip) the "at least one assessment
   *  question" guard. */
  atMinimumAssessmentQuestions?: boolean;
  competencies: ReviewPageCompetency[];
  emptyState?: TableEmptyState;
  header?: string;
  infoTooltipLabel?: string;
  isLegacyScreener: boolean;
  isReadOnly: boolean;
  isUnframed?: boolean;
  onCompetencyReorder: (
    updater: (competencies: ReviewPageCompetency[]) => ReviewPageCompetency[],
  ) => void;
  onDeleteCompetency: (competencyId: string, onSettled: () => void) => void;
  openCompetencyModal: (row: ReviewPageCompetency | null) => void;
  setSelectedCompetency: (competency: ReviewPageCompetency | null) => void;
  /** Override for the Format column visibility. Defaults to the
   *  voice2-questions flag when not provided. */
  showFormatColumn?: boolean;
}

export const AssessmentQuestionTable = ({
  addQuestionTestId,
  atMaxCompetencies,
  atMinimumAssessmentQuestions,
  competencies,
  emptyState,
  header,
  infoTooltipLabel,
  isLegacyScreener,
  isReadOnly,
  isUnframed = false,
  onCompetencyReorder,
  onDeleteCompetency,
  openCompetencyModal,
  setSelectedCompetency,
  showFormatColumn,
}: AssessmentQuestionTableProps) => {
  const { id: screenerId } = useParams<{ id?: string }>() ?? {};
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [
    warningModalOpened,
    { open: openWarningModal, close: closeWarningModal },
  ] = useDisclosure(false);
  const track = useTrackAnalytics({ screener_id: screenerId });
  const hasVoice2QuestionsFlag = useFeatureIsOn(FEATURE_FLAGS.voice2Questions);
  const onDragEnd = useGetDragEndEventHandler(onCompetencyReorder);

  const handleEdit = useCallback(
    (row: ReviewPageCompetency) => {
      setSelectedCompetency(row);
      openCompetencyModal(row);
    },
    [setSelectedCompetency, openCompetencyModal],
  );

  const confirmDelete = useCallback(() => {
    if (deleteId) {
      closeWarningModal();

      onDeleteCompetency(deleteId, () => {
        setDeleteId(null);
      });
    }
  }, [onDeleteCompetency, deleteId, closeWarningModal]);

  const handleDelete = (id: string) => {
    setDeleteId(id);
    openWarningModal();
  };

  const columns = formatAssessmentQuestionColumns(
    competencies,
    handleEdit,
    handleDelete,
    isReadOnly,
    track,
    {
      showFormatColumn: showFormatColumn ?? hasVoice2QuestionsFlag,
      atMinimumAssessmentQuestions,
    },
  );

  const alternativeColumns = formatAlternativeCompetencyColumns(handleEdit);

  const displayCompetencies = useMemo(() => {
    if (isReadOnly) {
      return competencies.filter((comp) => comp.isIncluded);
    }
    return competencies;
  }, [competencies, isReadOnly]);

  const atMaxIncludedCompetencies = useMemo(
    () => atMaxCompetencies ?? screenerHasMaxIncludedCompetencies(competencies),
    [atMaxCompetencies, competencies],
  );

  // AS-2019: multilingual no longer blocks voice questions, so the only reason
  // left to disable adding is the included-competency cap.
  const addQuestionDisabled = atMaxIncludedCompetencies;
  const addQuestionTooltip = MAX_COMPETENCIES_TOOLTIP_MESSAGE;

  const table = useReactTable({
    columns: isLegacyScreener ? alternativeColumns : columns,
    data: displayCompetencies,
    getCoreRowModel: getCoreRowModel(),
    getRowId: ({ id }) => id,
    state: {
      columnVisibility: getColumnVisibilityState(isReadOnly),
    },
  });

  return (
    <TableWrapper
      addQuestionDisabled={addQuestionDisabled}
      addQuestionTestId={
        addQuestionTestId ??
        buildTestId("builder-review", "assessment-questions", "add-question")
      }
      addQuestionTooltip={addQuestionTooltip}
      addQuestionTooltipDisabled={!addQuestionDisabled}
      closeWarningModal={closeWarningModal}
      confirmDelete={confirmDelete}
      emptyState={emptyState}
      hasRows={table.getRowModel().rows.length > 0}
      header={
        header ?? (isLegacyScreener ? "Competencies" : "Assessment questions")
      }
      isReadOnly={isReadOnly}
      isUnframed={isUnframed}
      onAddQuestionClick={() => {
        track("ADD COMPETENCY + Button Clicked");
        setSelectedCompetency(null);
        openCompetencyModal(null);
      }}
      tableHeaders={table.getFlatHeaders()}
      infoTooltipLabel={
        infoTooltipLabel ??
        "The question that should be used to assess this competency"
      }
      warningModalOpened={warningModalOpened}
    >
      <DragDropProvider onDragEnd={onDragEnd}>
        {table.getRowModel().rows.map((row, index) => (
          <SortableTableRow
            cellTDClassName={(cell) => [
              "min-h-14",
              "py-2",
              cell.column.id === "question" ? "pl-4 pr-0" : "px-4",
            ]}
            className={(row, isDragging) => [
              "bg-white",
              "border-gray-200",
              "border-t",
              "min-h-14",
              "p-0",
              isDragging && [
                "drop-shadow-[0_0_12px_rgba(0,0,0,0.10)]",
                "border-b",
              ],
            ]}
            disabled={isReadOnly}
            id={row.original.id}
            index={index}
            key={row.id}
            renderCell={(cell) =>
              flexRender(cell.column.columnDef.cell, cell.getContext())
            }
            row={row}
          />
        ))}
      </DragDropProvider>
    </TableWrapper>
  );
};
